import type { DealerInfoResponse, DealerProfileSection } from "@credit-acceptance/step6-bff-client";

import type { DealerInfoViewModel } from "./DealerInfoModal.types";

// ---------------------------------------------------------------------------
// dealerInfoModal.mapping
//
// Pure mapper: DealerInfoResponse → DealerInfoViewModel. No React.
// The Volume / Avg Days to Fund / contracts-in-AH block from the legacy
// popup has no producer in this endpoint (SVRS-41708) — those fields are
// left at their empty defaults; the section stays in the UI regardless.
// ---------------------------------------------------------------------------

/**
 * Builds the "Physical Location" display line from the dealer profile's
 * address fields: street lines, then "City, ST ZIP".
 * @param {DealerProfileSection | undefined} profile - The dealer profile block.
 * @returns {string} The combined physical location, or "".
 */
function buildPhysicalLocation(profile: DealerProfileSection | undefined): string {
  if (!profile) {
    return "";
  }
  const streetLines = [profile.addressLine1, profile.addressLine2].filter(Boolean).join(", ");
  const cityState = [profile.city, profile.state].filter(Boolean).join(", ");
  const cityStateZip = cityState && profile.zip ? `${cityState} ${profile.zip}` : cityState || profile.zip || "";
  return [streetLines, cityStateZip].filter(Boolean).join(", ");
}

/**
 * Formats the dealer's original activation date for display. The BFF passes
 * this through verbatim as an offset-less ISO timestamp (date, or
 * date+time) — only the date part is shown, as MM/DD/YYYY.
 * @param {string | undefined} iso - The raw activation timestamp.
 * @returns {string} The formatted date, e.g. "06/04/2024", or "".
 */
function formatActivationDate(iso: string | undefined): string {
  if (!iso) {
    return "";
  }
  const datePart = iso.split("T")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) {
    return "";
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

const PHONE_NUMBER_DIGITS = 10;

/**
 * Formats a phone/fax number as NANP `xxx-xxx-xxxx` when the raw value has
 * exactly 10 digits — dealer-management doesn't guarantee a consistent
 * format, so a value that arrives already-formatted is normalized the same
 * way. Any other digit count (extensions, malformed data) is left as-is
 * rather than truncated into a fake-looking number.
 * @param {string | undefined} raw - The raw phone/fax value.
 * @returns {string} The formatted number, or the original value, or "".
 */
function formatPhoneNumber(raw: string | undefined): string {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return "";
  }
  const digits = trimmed.replaceAll(/\D/g, "");
  if (digits.length !== PHONE_NUMBER_DIGITS) {
    return trimmed;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Normalizes the dealer website URL. Dealer-management stores the value as-is
 * with no scheme guarantee, so a bare host (e.g. "wrightsautoemporia.com") is
 * prefixed with "http://" for use as a link href; a value that already
 * carries a scheme is left untouched.
 * @param {string | undefined} website - The raw website value.
 * @returns {string} The normalized URL, or "".
 */
function normalizeWebsiteUrl(website: string | undefined): string {
  const trimmed = website?.trim();
  if (!trimmed) {
    return "";
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
}

/**
 * Maps a `DealerInfoResponse` to a `DealerInfoViewModel` for the Dealer
 * Information modal. Safe to call with a partial response — `otherContacts`
 * and `salesPerson` are individually omitted by the BFF when their lookup
 * fails, and every field falls back to "" (the modal renders "—" for that).
 * @param {DealerInfoResponse} response - The BFF dealer-info response.
 * @returns {DealerInfoViewModel} The mapped view model.
 */
export function mapDealerInfoResponseToViewModel(response: DealerInfoResponse): DealerInfoViewModel {
  const profile = response.dealerProfile;
  const salesPerson = response.salesPerson;
  const otherContacts = response.otherContacts;

  return {
    // The BFF's `nameOnContract` is unconfirmed against real data (may be a
    // selector rather than a name) — `legalName` is the reliable dealer name,
    // so it's what the modal's "Name on Contract" row actually shows.
    nameOnContract: profile?.legalName ?? "",
    dba: profile?.dba ?? "",
    physicalLocation: buildPhysicalLocation(profile),
    dealershipNumber: formatPhoneNumber(profile?.dealershipPhoneNumber),
    dealerFax: formatPhoneNumber(profile?.faxNumber),
    dealerRating:
      profile?.dealerRating !== undefined && profile.dealerRating !== null ? profile.dealerRating.toFixed(1) : "",
    originalActivationDate: formatActivationDate(profile?.originalActivationDate),
    websiteUrl: normalizeWebsiteUrl(profile?.website),
    salesPersonName: [salesPerson?.firstName, salesPerson?.lastName].filter(Boolean).join(" "),
    salesPersonNumber: formatPhoneNumber(salesPerson?.officePhoneNumber),
    contractsInAh: "",
    volumeMonths: [],
    volumeTotal: "",
    avgDaysToFundTotal: "",
    mamName: otherContacts?.mamName ?? "",
    mamPhone: formatPhoneNumber(otherContacts?.mamPhoneNumber),
    dosName: otherContacts?.dosName ?? "",
    dosPhone: formatPhoneNumber(otherContacts?.dosPhoneNumber),
  };
}
